import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { upgradeDataPropsInterface } from 'ui-addon-upgrades/_core/common/data/upgradeData/interfaces/upgradeDataPropsInterface';
import TrialActivationNextStepsModal from './TrialActivationNextStepsModal';
import ActivationSuccessModal from 'ui-addon-upgrades/_core/common/components/activation/ActivationSuccessModal';
import { sourceKeys } from 'ui-addon-upgrades/_core/common/data/upgradeData/properties/sources';
import { NEXT_STEPS_SURVEY_API_NAMES, NEXT_STEPS_SURVEY_UPGRADE_PRODUCTS } from 'ui-addon-upgrades/_core/common/constants/trials';
import { navigateTo } from '../../common/navigation/url';
import { getTrialGuideUrl, getTrialGuideUrlFromApiName } from '../../utils/links';
import { refresh } from '../../utils/links';
import { trialIdsWithTrialGuide, upgradeProductToTrialIds, apiNameToTrialIds } from 'self-service-api/constants/Trials';
import Raven from 'raven-js';
import { trackFailedApiNameMap } from '../../utils/metricUtils';
const SUCCESS_MODAL_TYPES = {
  INITIAL_SUCCESS: 'INITIAL_SUCCESS',
  NEXT_STEPS_SURVEY: 'NEXT_STEPS_SURVEY'
};

const TrialActivationSuccessContainer = ({
  hideBackground,
  header,
  subText,
  ctaText,
  onSuccess,
  upgradeData,
  shouldHideSurvey,
  showIllustration,
  showTrialLoadingState,
  canViewNewSeats
}) => {
  const {
    upgradeProduct,
    apiName,
    app,
    screen,
    uniqueId
  } = upgradeData;
  const [successModalType, setSuccessModalType] = useState(SUCCESS_MODAL_TYPES.INITIAL_SUCCESS);
  const eligibleForNewSeats = canViewNewSeats && apiName;
  const defaultOnSuccess = useCallback(() => {
    const getTrialId = () => {
      if (eligibleForNewSeats && apiNameToTrialIds[apiName]) {
        return apiNameToTrialIds[apiName];
      } else {
        if (eligibleForNewSeats) {
          trackFailedApiNameMap('getTrialId', apiName, upgradeProduct);
          Raven.captureMessage(`failed to get trial id from apiName ${apiName} in getTrialId`);
        }

        return upgradeProductToTrialIds[upgradeProduct];
      }
    };

    const trialId = getTrialId();

    if (eligibleForNewSeats && trialId && trialIdsWithTrialGuide.includes(trialId)) {
      getTrialGuideUrlFromApiName(apiName, `${app}-${screen}-${uniqueId}`);
    } else if (trialId && trialIdsWithTrialGuide.includes(trialId)) {
      navigateTo(getTrialGuideUrl(upgradeProduct, `${app}-${screen}-${uniqueId}`));
    } else {
      refresh();
    }
  }, [app, screen, uniqueId, upgradeProduct, apiName, eligibleForNewSeats]);

  const maybeShowNextStepsSurvey = () => {
    const nextStepsIncludesProduct = eligibleForNewSeats ? NEXT_STEPS_SURVEY_API_NAMES.includes(apiName) : NEXT_STEPS_SURVEY_UPGRADE_PRODUCTS.includes(upgradeProduct);
    const shouldShowNextStepsSurvey = nextStepsIncludesProduct && !shouldHideSurvey;

    if (shouldShowNextStepsSurvey) {
      return setSuccessModalType(SUCCESS_MODAL_TYPES.NEXT_STEPS_SURVEY);
    }

    if (onSuccess) {
      return onSuccess();
    }

    return defaultOnSuccess();
  };

  switch (successModalType) {
    case SUCCESS_MODAL_TYPES.NEXT_STEPS_SURVEY:
      return /*#__PURE__*/_jsx(TrialActivationNextStepsModal, {
        upgradeData: upgradeData,
        canViewNewSeats: canViewNewSeats
      });

    case SUCCESS_MODAL_TYPES.INITIAL_SUCCESS:
      return /*#__PURE__*/_jsx(ActivationSuccessModal, {
        type: sourceKeys.TRIAL,
        hideBackground: hideBackground,
        header: header,
        subText: subText,
        ctaText: ctaText,
        showIllustration: showIllustration,
        showTrialLoadingState: showTrialLoadingState,
        onSuccess: maybeShowNextStepsSurvey,
        upgradeData: upgradeData,
        canViewNewSeats: canViewNewSeats
      });

    default:
      return null;
  }
};

TrialActivationSuccessContainer.propTypes = Object.assign({
  hideBackground: PropTypes.bool.isRequired,
  header: PropTypes.node,
  subText: PropTypes.node,
  ctaText: PropTypes.node,
  showIllustration: PropTypes.bool,
  shouldHideSurvey: PropTypes.bool,
  onSuccess: PropTypes.func,
  showTrialLoadingState: PropTypes.bool
}, upgradeDataPropsInterface);
export default TrialActivationSuccessContainer;